/**
 * Water Table 3D Visualization
 *
 * Renders a 3D view of the water table surface using voxel-style rendering.
 * Wells are plotted at accurate depth. Water table surface uses IDW interpolation
 * from well gauging data, rendered as a blue translucent mesh.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { OrbitControls, Text } from '@react-three/drei';
import * as THREE from 'three';
import type { WellPoint } from '../../api/phreatic';
import { generateContours } from './ContourGenerator';

// Scale: 1 unit = 10 feet
const SCALE = 0.1;
const FT_PER_DEG_LAT = 364000;
const FT_PER_DEG_LON_AT_32 = 308000; // ~32° latitude (Texas)

interface PredictedWell {
  well_id: string;
  well_name: string;
  steps: Array<{
    target_date: string;
    p50: number;
    depth_to_water_ft: number | null;
    cavitation_risk: number;
  }>;
  trend: string;
}

interface WaterTable3DViewProps {
  wells: WellPoint[];
  centerLat: number;
  centerLon: number;
  flowDirection?: number | null;
  predictedWells?: PredictedWell[];
  predictionDateIndex?: number;
  /** Currently selected date from time slider */
  selectedDate?: string | null;
  siteAcres?: number;
  propertyBounds?: { north: number; south: number; east: number; west: number } | null;
  /** When set, only wells in this aquifer are shown. Wells with null/"other" aquifer pool with the active aquifer. */
  aquiferFilter?: string | null;
}

// ── IDW Interpolation ─────────────────────────────────────────────────

function idwInterpolate(
  x: number, y: number,
  points: { x: number; y: number; val: number }[],
  power: number = 2
): number {
  let weightSum = 0;
  let valueSum = 0;
  for (const p of points) {
    const dist = Math.sqrt((x - p.x) ** 2 + (y - p.y) ** 2);
    if (dist < 0.001) return p.val;
    const w = 1 / dist ** power;
    weightSum += w;
    valueSum += w * p.val;
  }
  return weightSum > 0 ? valueSum / weightSum : 0;
}

// ── Well Column Component ─────────────────────────────────────────────

function getCavitationColor(risk: number): string {
  if (risk >= 0.8) return '#ef4444';  // red
  if (risk >= 0.5) return '#f97316';  // orange
  if (risk >= 0.2) return '#f59e0b';  // yellow
  return '#10b981';                    // green
}

function getDepletionColor(ratePerYear: number): [number, number, number] {
  if (ratePerYear > 5) return [0.94, 0.27, 0.27];     // red
  if (ratePerYear > 2) return [0.98, 0.58, 0.10];     // orange
  if (ratePerYear > 0.5) return [0.96, 0.75, 0.03];   // yellow
  return [0.06, 0.72, 0.51];                            // green
}

function WellColumn({ well, cx, cy, baseElev, waterElev, cavitationRisk, markerScale = 1 }: {
  well: WellPoint;
  cx: number;
  cy: number;
  baseElev: number;
  waterElev: number;
  cavitationRisk?: number;
  markerScale?: number;
}) {
  const groundElev = well.ground_elevation_ft ?? waterElev + (well.depth_to_water_ft ?? 10);
  const totalDepth = well.total_depth_ft ?? (well.depth_to_water_ft ? well.depth_to_water_ft * 1.5 : 50);
  const topZ = (groundElev - baseElev) * SCALE;
  const bottomElev = groundElev - totalDepth;
  const bottomZ = (bottomElev - baseElev) * SCALE;
  const casingHeight = topZ - bottomZ;
  const casingCenterY = (topZ + bottomZ) / 2;
  const waterZ = (waterElev - baseElev) * SCALE;

  // Use cavitation risk color if available, otherwise default behavior
  const markerColor = cavitationRisk != null
    ? getCavitationColor(cavitationRisk)
    : well.is_dry ? '#ef4444' : (well.product_thickness_ft && well.product_thickness_ft > 0 ? '#f59e0b' : '#06b6d4');
  const emissiveColor = cavitationRisk != null
    ? getCavitationColor(cavitationRisk)
    : well.is_dry ? '#ef4444' : '#06b6d4';

  return (
    <group position={[cx, 0, cy]}>
      {/* Well casing — extends from ground surface down to total depth */}
      <mesh position={[0, casingCenterY, 0]}>
        <cylinderGeometry args={[0.15 * markerScale, 0.15 * markerScale, Math.max(casingHeight, 0.5), 8]} />
        <meshStandardMaterial color="#888" transparent opacity={0.6} />
      </mesh>
      {/* Bottom cap — marks total depth */}
      <mesh position={[0, bottomZ, 0]}>
        <sphereGeometry args={[0.2 * markerScale, 6, 6]} />
        <meshStandardMaterial color="#555" />
      </mesh>
      {/* Well head marker — at ground surface */}
      <mesh position={[0, topZ + 0.3 * markerScale, 0]}>
        <sphereGeometry args={[0.3 * markerScale, 8, 8]} />
        <meshStandardMaterial
          color={markerColor}
          emissive={emissiveColor}
          emissiveIntensity={0.3}
        />
      </mesh>
      {/* Water level indicator (blue ring) */}
      {well.water_elevation_ft != null && (
        <mesh position={[0, waterZ, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.25 * markerScale, 0.05 * markerScale, 8, 16]} />
          <meshStandardMaterial color="#60a5fa" emissive="#60a5fa" emissiveIntensity={0.5} />
        </mesh>
      )}
      {/* Label */}
      <Text
        position={[0, topZ + 1 * markerScale, 0]}
        fontSize={0.4 * markerScale}
        color="#ddd"
        anchorX="center"
        anchorY="bottom"
      >
        {well.well_name}
      </Text>
    </group>
  );
}

// ── Water Table Surface ───────────────────────────────────────────────

function WaterTableSurface({ wells, baseElev, gridSize, depletionMode }: {
  wells: { x: number; y: number; waterElev: number; depletionRate?: number }[];
  baseElev: number;
  gridSize: number;
  depletionMode?: boolean;
}) {
  const geometry = useMemo(() => {
    if (wells.length < 3) return null;

    const xs = wells.map(w => w.x);
    const ys = wells.map(w => w.y);
    const minX = Math.min(...xs) - 5;
    const maxX = Math.max(...xs) + 5;
    const minY = Math.min(...ys) - 5;
    const maxY = Math.max(...ys) + 5;

    const res = gridSize;
    const dx = (maxX - minX) / (res - 1);
    const dy = (maxY - minY) / (res - 1);

    const points = wells.map(w => ({ x: w.x, y: w.y, val: w.waterElev }));

    const vertices: number[] = [];
    const indices: number[] = [];
    const colors: number[] = [];

    const elevs = wells.map(w => w.waterElev);
    const minElev = Math.min(...elevs);
    const maxElev = Math.max(...elevs);
    const range = maxElev - minElev || 1;

    for (let i = 0; i < res; i++) {
      for (let j = 0; j < res; j++) {
        const gx = minX + i * dx;
        const gy = minY + j * dy;
        const elev = idwInterpolate(gx, gy, points);
        const z = (elev - baseElev) * SCALE;

        vertices.push(gx, z, gy);

        // Color: depletion mode uses green→red, default uses blue gradient
        if (depletionMode && wells.some(w => w.depletionRate != null)) {
          const deplPoints = wells.filter(w => w.depletionRate != null).map(w => ({
            x: w.x, y: w.y, val: w.depletionRate!,
          }));
          const rate = deplPoints.length > 0 ? idwInterpolate(gx, gy, deplPoints) : 0;
          const [r, g, b] = getDepletionColor(Math.abs(rate));
          colors.push(r, g, b);
        } else {
          const t = (elev - minElev) / range;
          colors.push(
            0.1 + t * 0.2,   // R
            0.3 + t * 0.3,   // G
            0.7 + t * 0.3,   // B
          );
        }
      }
    }

    for (let i = 0; i < res - 1; i++) {
      for (let j = 0; j < res - 1; j++) {
        const a = i * res + j;
        const b = a + 1;
        const c = (i + 1) * res + j;
        const d = c + 1;
        indices.push(a, c, b);
        indices.push(b, c, d);
      }
    }

    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geom.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    geom.setIndex(indices);
    geom.computeVertexNormals();
    return geom;
  }, [wells, baseElev, gridSize, depletionMode]);

  // Animate subtle wave effect
  const meshRef = useRef<THREE.Mesh>(null);
  useFrame(({ clock }) => {
    if (meshRef.current) {
      meshRef.current.position.y = Math.sin(clock.elapsedTime * 0.5) * 0.02;
    }
  });

  if (!geometry) return null;

  return (
    <mesh ref={meshRef} geometry={geometry}>
      <meshStandardMaterial
        vertexColors
        transparent
        opacity={0.6}
        side={THREE.DoubleSide}
        roughness={0.3}
        metalness={0.1}
      />
    </mesh>
  );
}

// ── Aquifer Bottom Surface (well_bottom = ground_elev - total_depth) ──

function AquiferBottomSurface({ wells, baseElev, gridSize }: {
  wells: { x: number; y: number; bottomElev: number }[];
  baseElev: number;
  gridSize: number;
}) {
  const geometry = useMemo(() => {
    if (wells.length < 3) return null;
    const xs = wells.map(w => w.x);
    const ys = wells.map(w => w.y);
    const minX = Math.min(...xs) - 5, maxX = Math.max(...xs) + 5;
    const minY = Math.min(...ys) - 5, maxY = Math.max(...ys) + 5;
    const res = gridSize;
    const dx = (maxX - minX) / (res - 1), dy = (maxY - minY) / (res - 1);
    const points = wells.map(w => ({ x: w.x, y: w.y, val: w.bottomElev }));
    const vertices: number[] = [], indices: number[] = [];

    for (let i = 0; i < res; i++) {
      for (let j = 0; j < res; j++) {
        const gx = minX + i * dx, gy = minY + j * dy;
        const elev = idwInterpolate(gx, gy, points);
        vertices.push(gx, (elev - baseElev) * SCALE, gy);
      }
    }
    for (let i = 0; i < res - 1; i++) {
      for (let j = 0; j < res - 1; j++) {
        const a = i * res + j;
        indices.push(a, (i + 1) * res + j, a + 1);
        indices.push(a + 1, (i + 1) * res + j, (i + 1) * res + j + 1);
      }
    }
    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geom.setIndex(indices);
    geom.computeVertexNormals();
    return geom;
  }, [wells, baseElev, gridSize]);

  if (!geometry) return null;
  return (
    <mesh geometry={geometry}>
      <meshStandardMaterial color="#8B2500" transparent opacity={0.2} side={THREE.DoubleSide} depthWrite={false} />
    </mesh>
  );
}

// ── Saturated Zone Volume (blue translucent slab between water table and aquifer bottom) ──

function SaturatedVolume({ wells, baseElev, gridSize }: {
  wells: { x: number; y: number; waterElev: number; bottomElev: number }[];
  baseElev: number;
  gridSize: number;
}) {
  const geometry = useMemo(() => {
    if (wells.length < 3) return null;
    const xs = wells.map(w => w.x);
    const ys = wells.map(w => w.y);
    const minX = Math.min(...xs) - 5, maxX = Math.max(...xs) + 5;
    const minY = Math.min(...ys) - 5, maxY = Math.max(...ys) + 5;
    const res = gridSize;
    const dx = (maxX - minX) / (res - 1), dy = (maxY - minY) / (res - 1);

    const waterPts = wells.map(w => ({ x: w.x, y: w.y, val: w.waterElev }));
    const bottomPts = wells.map(w => ({ x: w.x, y: w.y, val: w.bottomElev }));

    // Build a closed volume: top face (water table) + bottom face (aquifer base) + sides
    const vertices: number[] = [];
    const indices: number[] = [];
    const colors: number[] = [];

    // Top face (water table)
    for (let i = 0; i < res; i++) {
      for (let j = 0; j < res; j++) {
        const gx = minX + i * dx, gy = minY + j * dy;
        const waterZ = (idwInterpolate(gx, gy, waterPts) - baseElev) * SCALE;
        vertices.push(gx, waterZ, gy);
        colors.push(0.15, 0.45, 0.85); // blue top
      }
    }
    // Bottom face (aquifer base) — offset by res*res vertices
    for (let i = 0; i < res; i++) {
      for (let j = 0; j < res; j++) {
        const gx = minX + i * dx, gy = minY + j * dy;
        const bottomZ = (idwInterpolate(gx, gy, bottomPts) - baseElev) * SCALE;
        vertices.push(gx, bottomZ, gy);
        colors.push(0.1, 0.25, 0.55); // darker blue bottom
      }
    }

    const n = res * res;
    // Top face triangles
    for (let i = 0; i < res - 1; i++) {
      for (let j = 0; j < res - 1; j++) {
        const a = i * res + j;
        indices.push(a, (i + 1) * res + j, a + 1);
        indices.push(a + 1, (i + 1) * res + j, (i + 1) * res + j + 1);
      }
    }
    // Bottom face triangles (reversed winding for outward normals)
    for (let i = 0; i < res - 1; i++) {
      for (let j = 0; j < res - 1; j++) {
        const a = n + i * res + j;
        indices.push(a, a + 1, n + (i + 1) * res + j);
        indices.push(a + 1, n + (i + 1) * res + j + 1, n + (i + 1) * res + j);
      }
    }
    // Side walls — connect top edge to bottom edge around perimeter
    for (let i = 0; i < res - 1; i++) {
      // Front edge (j=0)
      const tf = i * res, bf = n + i * res;
      const tfn = (i + 1) * res, bfn = n + (i + 1) * res;
      indices.push(tf, bf, tfn); indices.push(tfn, bf, bfn);
      // Back edge (j=res-1)
      const tb = i * res + res - 1, bb = n + i * res + res - 1;
      const tbn = (i + 1) * res + res - 1, bbn = n + (i + 1) * res + res - 1;
      indices.push(tb, tbn, bb); indices.push(tbn, bbn, bb);
    }
    for (let j = 0; j < res - 1; j++) {
      // Left edge (i=0)
      const tl = j, bl = n + j;
      const tln = j + 1, bln = n + j + 1;
      indices.push(tl, tln, bl); indices.push(tln, bln, bl);
      // Right edge (i=res-1)
      const tr = (res - 1) * res + j, br = n + (res - 1) * res + j;
      const trn = (res - 1) * res + j + 1, brn = n + (res - 1) * res + j + 1;
      indices.push(tr, br, trn); indices.push(trn, br, brn);
    }

    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geom.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    geom.setIndex(indices);
    geom.computeVertexNormals();
    return geom;
  }, [wells, baseElev, gridSize]);

  if (!geometry) return null;
  return (
    <mesh geometry={geometry}>
      <meshStandardMaterial vertexColors transparent opacity={0.35} side={THREE.DoubleSide} roughness={0.4} depthWrite={false} />
    </mesh>
  );
}

// ── Ground Surface ────────────────────────────────────────────────────

function GroundSurface({ wells, baseElev, gridSize }: {
  wells: { x: number; y: number; groundElev: number }[];
  baseElev: number;
  gridSize: number;
}) {
  const geometry = useMemo(() => {
    if (wells.length < 3) return null;

    const xs = wells.map(w => w.x);
    const ys = wells.map(w => w.y);
    const minX = Math.min(...xs) - 5;
    const maxX = Math.max(...xs) + 5;
    const minY = Math.min(...ys) - 5;
    const maxY = Math.max(...ys) + 5;

    const res = gridSize;
    const dx = (maxX - minX) / (res - 1);
    const dy = (maxY - minY) / (res - 1);

    const points = wells.map(w => ({ x: w.x, y: w.y, val: w.groundElev }));

    const vertices: number[] = [];
    const indices: number[] = [];

    for (let i = 0; i < res; i++) {
      for (let j = 0; j < res; j++) {
        const gx = minX + i * dx;
        const gy = minY + j * dy;
        const elev = idwInterpolate(gx, gy, points);
        vertices.push(gx, (elev - baseElev) * SCALE, gy);
      }
    }

    for (let i = 0; i < res - 1; i++) {
      for (let j = 0; j < res - 1; j++) {
        const a = i * res + j;
        const b = a + 1;
        const c = (i + 1) * res + j;
        const d = c + 1;
        indices.push(a, c, b);
        indices.push(b, c, d);
      }
    }

    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3));
    geom.setIndex(indices);
    geom.computeVertexNormals();
    return geom;
  }, [wells, baseElev, gridSize]);

  if (!geometry) return null;

  return (
    <mesh geometry={geometry}>
      <meshStandardMaterial
        color="#8B7355"
        transparent
        opacity={0.2}
        side={THREE.DoubleSide}
        depthWrite={false}
      />
    </mesh>
  );
}

// ── 3D Contour Lines on Water Table ──────────────────────────────────

function WaterTableContours3D({ wells, centerLat, centerLon, baseElev }: {
  wells: WellPoint[];
  centerLat: number;
  centerLon: number;
  baseElev: number;
}) {
  const lines = useMemo(() => {
    const wellElevs = wells
      .filter(w => w.latitude && w.longitude && w.water_elevation_ft != null && w.water_elevation_ft > 50)
      .map(w => ({ lat: w.latitude, lon: w.longitude, waterElev: w.water_elevation_ft! }));

    const contours = generateContours(wellElevs, 30, 8);

    return contours.map(cl => {
      const positions = cl.points.map(([lat, lon]) => {
        const x = (lon - centerLon) * FT_PER_DEG_LON_AT_32 * SCALE;
        const z = (lat - centerLat) * FT_PER_DEG_LAT * SCALE;
        const y = (cl.elevation - baseElev) * SCALE + 0.05; // slight offset above surface
        return new THREE.Vector3(x, y, z);
      });

      const geometry = new THREE.BufferGeometry().setFromPoints(positions);
      return { geometry, elevation: cl.elevation };
    });
  }, [wells, centerLat, centerLon, baseElev]);

  return (
    <group>
      {lines.map((line, i) => {
        const mat = new THREE.LineBasicMaterial({ color: '#ffffff', transparent: true, opacity: 0.5 });
        const lineObj = new THREE.Line(line.geometry, mat);
        return <primitive key={i} object={lineObj} />;
      })}
    </group>
  );
}

// ── Auto-fit camera to scene ──────────────────────────────────────────
function AutoFitCamera({ wellCoords, baseElev, fitKey }: {
  wellCoords: { x: number; y: number; groundElev: number; waterElev: number }[];
  baseElev: number;
  fitKey: string | number;
}) {
  const { camera } = useThree();
  const lastFitKey = useRef<string | number | null>(null);

  useEffect(() => {
    if (lastFitKey.current === fitKey || wellCoords.length === 0) return;
    lastFitKey.current = fitKey;

    const xs = wellCoords.map(w => w.x);
    const ys = wellCoords.map(w => w.y);
    // Center vertically on water table mean (what user wants framed)
    const meanWater = wellCoords.reduce((s, w) => s + w.waterElev, 0) / wellCoords.length;
    const cz = (meanWater - baseElev) * SCALE;

    const cx = (Math.min(...xs) + Math.max(...xs)) / 2;
    const cy = (Math.min(...ys) + Math.max(...ys)) / 2;

    const spanX = Math.max(...xs) - Math.min(...xs);
    const spanY = Math.max(...ys) - Math.min(...ys);
    const groundZs = wellCoords.map(w => (w.groundElev - baseElev) * SCALE);
    const spanZ = Math.max(...groundZs) - Math.min(...groundZs);
    const maxSpan = Math.max(spanX, spanY, spanZ, 10);

    const dist = maxSpan * 1.2;
    camera.position.set(cx + dist * 0.6, cz + dist * 0.5, cy + dist * 0.6);
    camera.lookAt(cx, cz, cy);
    camera.updateProjectionMatrix();
  }, [wellCoords, baseElev, camera, fitKey]);

  return null;
}

// ── Property Boundary (cyan rectangle in 3D) ────────────────────────

function PropertyBoundary({ centerX, centerY, sideX, sideY, elevation }: {
  centerX: number; centerY: number; sideX: number; sideY: number; elevation: number;
}) {
  const points = useMemo(() => {
    const hx = sideX / 2;
    const hy = sideY / 2;
    return [
      new THREE.Vector3(centerX - hx, elevation, centerY - hy),
      new THREE.Vector3(centerX + hx, elevation, centerY - hy),
      new THREE.Vector3(centerX + hx, elevation, centerY + hy),
      new THREE.Vector3(centerX - hx, elevation, centerY + hy),
      new THREE.Vector3(centerX - hx, elevation, centerY - hy),
    ];
  }, [centerX, centerY, sideX, sideY, elevation]);

  const geometry = useMemo(() => {
    const geo = new THREE.BufferGeometry().setFromPoints(points);
    return geo;
  }, [points]);

  return (
    <line geometry={geometry}>
      <lineBasicMaterial color="#06b6d4" linewidth={2} transparent opacity={0.8} />
    </line>
  );
}

// ── Double-Click: zoom camera tight on property boundary ────────────

function PropertyZoom({ targetX, targetY, targetZ, propSideX, propSideY, zoomed }: {
  targetX: number; targetY: number; targetZ: number;
  propSideX: number; propSideY: number; zoomed: boolean;
}) {
  const { camera } = useThree();
  const originalPos = useRef(new THREE.Vector3());
  const originalFov = useRef(50);
  const saved = useRef(false);

  useEffect(() => {
    if (zoomed) {
      if (!saved.current) {
        originalPos.current.copy(camera.position);
        originalFov.current = (camera as THREE.PerspectiveCamera).fov || 50;
        saved.current = true;
      }
      // Position camera close to property, looking down at it
      const maxSide = Math.max(propSideX, propSideY);
      const dist = maxSide * 0.5;
      camera.position.set(targetX + dist * 0.3, targetZ + dist * 0.5, targetY + dist * 0.3);
      camera.lookAt(targetX, targetZ, targetY);
      (camera as THREE.PerspectiveCamera).fov = 30;
      camera.updateProjectionMatrix();
    } else if (saved.current) {
      camera.position.copy(originalPos.current);
      (camera as THREE.PerspectiveCamera).fov = originalFov.current;
      camera.lookAt(targetX, targetZ, targetY);
      camera.updateProjectionMatrix();
      saved.current = false;
    }
  }, [zoomed, camera, targetX, targetY, targetZ, propSideX, propSideY]);

  return null;
}

// ── Main 3D View ──────────────────────────────────────────────────────

export function WaterTable3DView({ wells, centerLat, centerLon, flowDirection, predictedWells, predictionDateIndex, selectedDate, siteAcres = 640, propertyBounds, aquiferFilter }: WaterTable3DViewProps) {
  const [showGround, setShowGround] = useState(true);
  const [showWater, setShowWater] = useState(true);
  const [showRedBed, setShowRedBed] = useState(true);
  const [propertyClipped, setPropertyClipped] = useState(false);

  // Aquifer-filtered wells. Exact case-insensitive match: Ogallala wells build
  // the Ogallala table, Dockum wells build the Dockum table. No pooling.
  const filteredWells = useMemo(() => {
    if (!aquiferFilter || aquiferFilter === '__combined__') return wells;
    const target = aquiferFilter.toLowerCase().trim();
    return wells.filter(w => (w.aquifer_name ?? '').toLowerCase().trim() === target);
  }, [wells, aquiferFilter]);

  const isCombined = aquiferFilter === '__combined__';
  // Build prediction lookup
  const predictionMap = useMemo(() => {
    const map = new Map<string, { waterElev: number; cavitationRisk: number; depletionRate: number }>();
    if (!predictedWells || predictionDateIndex == null || predictionDateIndex < 0) return map;
    for (const pw of predictedWells) {
      const step = pw.steps[predictionDateIndex];
      if (!step) continue;
      // Compute annual depletion rate from first to current step
      const firstElev = pw.steps[0]?.p50 ?? step.p50;
      const yearsElapsed = ((predictionDateIndex + 1) * 90) / 365;
      const rate = yearsElapsed > 0 ? (firstElev - step.p50) / yearsElapsed : 0;
      map.set(pw.well_name, {
        waterElev: step.p50,
        cavitationRisk: step.cavitation_risk,
        depletionRate: rate,
      });
    }
    return map;
  }, [predictedWells, predictionDateIndex]);

  const hasPredictions = predictionMap.size > 0;

  // Convert wells to 3D coordinates
  const { wellCoords, baseElev } = useMemo(() => {
    const validWells = filteredWells.filter(w =>
      w.latitude && w.longitude && w.water_elevation_ft != null && w.water_elevation_ft > 50
    );
    if (validWells.length === 0) return { wellCoords: [], baseElev: 0 };

    const elevs = validWells.map(w => {
      const pred = predictionMap.get(w.well_name);
      return pred ? pred.waterElev : w.water_elevation_ft!;
    });
    const base = Math.min(...elevs) - 20;

    const coords = validWells.map(w => {
      const pred = predictionMap.get(w.well_name);
      const ge = w.ground_elevation_ft ?? (w.water_elevation_ft! + (w.depth_to_water_ft ?? 10));
      const td = w.total_depth_ft ?? (w.depth_to_water_ft ? w.depth_to_water_ft * 1.5 : 100);
      return {
        well: w,
        x: (w.longitude - centerLon) * FT_PER_DEG_LON_AT_32 * SCALE,
        y: (w.latitude - centerLat) * FT_PER_DEG_LAT * SCALE,
        waterElev: pred ? pred.waterElev : w.water_elevation_ft!,
        groundElev: ge,
        bottomElev: ge - td,  // aquifer base = ground minus total depth
        cavitationRisk: pred?.cavitationRisk,
        depletionRate: pred?.depletionRate,
      };
    });

    // Base elevation from deepest well bottom (not water table)
    const allBottoms = coords.map(w => w.bottomElev);
    const deepBase = Math.min(...allBottoms) - 50; // 50ft padding below deepest well

    return { wellCoords: coords, baseElev: deepBase };
  }, [filteredWells, centerLat, centerLon, predictionMap]);

  // Combined mode: group wells by aquifer. The aquifer label on each well is
  // already the unified effective_aquifer (resolved once by the backend spatial
  // KNN classifier at snip time), so this is a simple group-by — no per-render
  // reclassification. Each group also carries a median saturated thickness so
  // the aquifer base renders as a parallel surface below the water table.
  const aquiferGroups = useMemo(() => {
    if (!isCombined) return null;
    const map = new Map<string, typeof wellCoords>();
    for (const wc of wellCoords) {
      const raw = (wc.well.aquifer_name ?? '').trim();
      const key = !raw || raw.toLowerCase() === 'unknown' ? 'Other' : raw;
      const arr = map.get(key) ?? [];
      arr.push(wc);
      map.set(key, arr);
    }
    return Array.from(map.entries()).map(([name, coords]) => {
      const thicknesses = coords
        .map(w => w.waterElev - w.bottomElev)
        .filter(t => t > 0 && isFinite(t))
        .sort((a, b) => a - b);
      const medianThickness = thicknesses.length > 0
        ? thicknesses[Math.floor(thicknesses.length / 2)]
        : 100;
      return { name, coords, medianThickness };
    });
  }, [isCombined, wellCoords]);

  // Compute marker scale based on scene size so wells are visible at any zoom
  const markerScale = useMemo(() => {
    if (wellCoords.length < 2) return 1;
    const xs = wellCoords.map(w => w.x);
    const ys = wellCoords.map(w => w.y);
    const span = Math.max(
      Math.max(...xs) - Math.min(...xs),
      Math.max(...ys) - Math.min(...ys),
      10,
    );
    // Scale markers proportional to scene: base size at span=60, grows with scene
    return Math.max(1, span / 60);
  }, [wellCoords]);

  if (wellCoords.length < 3) {
    return (
      <div className="bg-mono-900 border border-mono-800 rounded-lg p-6 text-center h-[500px] flex items-center justify-center">
        <p className="text-mono-500 text-sm">Need at least 3 wells with water elevation data for 3D view</p>
      </div>
    );
  }

  return (
    <div className="bg-mono-900 border border-mono-800 rounded-lg overflow-hidden">
      <div className="flex items-center justify-between p-3 border-b border-mono-800">
        <h3 className="text-sm font-semibold text-mono-200 uppercase tracking-wider">
          3D Water Table
          {hasPredictions && <span className="ml-2 text-cyan-400 text-[10px] font-normal">PREDICTED</span>}
        </h3>
        <div className="flex items-center gap-4">
          {hasPredictions && (
            <div className="flex items-center gap-2 text-[10px]">
              <span className="text-mono-500">Depletion:</span>
              <span className="text-cyan-400">Stable</span>
              <span className="text-cyan-400">Moderate</span>
              <span className="text-orange-400">Significant</span>
              <span className="text-red-400">Rapid</span>
            </div>
          )}
          <div className="flex items-center gap-2 text-[9px]">
            <button
              onClick={() => setShowGround(v => !v)}
              className={`flex items-center gap-1 px-1.5 py-0.5 rounded border transition-all ${
                showGround
                  ? 'border-cyan-700/50 bg-cyan-900/30 text-mono-300'
                  : 'border-mono-700 bg-mono-800/50 text-mono-600 line-through'
              }`}
            >
              <span className="inline-block w-3 h-2 rounded-sm" style={{ background: showGround ? 'rgba(139,115,85,0.6)' : 'rgba(139,115,85,0.2)' }} />
              Surface
            </button>
            <button
              onClick={() => setShowWater(v => !v)}
              className={`flex items-center gap-1 px-1.5 py-0.5 rounded border transition-all ${
                showWater
                  ? 'border-blue-500/50 bg-blue-900/30 text-mono-300'
                  : 'border-mono-700 bg-mono-800/50 text-mono-600 line-through'
              }`}
            >
              <span className="inline-block w-3 h-2 rounded-sm" style={{ background: showWater ? 'rgba(38,115,216,0.6)' : 'rgba(38,115,216,0.2)' }} />
              Water Table
            </button>
            <button
              onClick={() => setShowRedBed(v => !v)}
              className={`flex items-center gap-1 px-1.5 py-0.5 rounded border transition-all ${
                showRedBed
                  ? 'border-red-800/50 bg-red-900/30 text-mono-300'
                  : 'border-mono-700 bg-mono-800/50 text-mono-600 line-through'
              }`}
            >
              <span className="inline-block w-3 h-2 rounded-sm" style={{ background: showRedBed ? 'rgba(139,37,0,0.5)' : 'rgba(139,37,0,0.2)' }} />
              Red Bed
            </button>
            <button
              onClick={() => setPropertyClipped(v => !v)}
              className={`flex items-center gap-1 px-1.5 py-0.5 rounded border transition-all ${
                propertyClipped
                  ? 'border-cyan-500/50 bg-cyan-500/20 text-cyan-300'
                  : 'border-mono-700 bg-mono-800/50 text-mono-500'
              }`}
            >
              <span className="inline-block w-3 h-2 rounded-sm border" style={{ borderColor: '#06b6d4', opacity: propertyClipped ? 1 : 0.4 }} />
              Property
            </button>
            <span className="text-mono-600 ml-1">{propertyClipped ? 'Dbl-click to zoom out' : 'Dbl-click property'}</span>
            {flowDirection != null && (
              <span className="text-blue-400">Flow: {flowDirection.toFixed(0)}°</span>
            )}
            {isCombined && aquiferGroups && (() => {
              const watchIds = new Set(['761228', '1006220']);
              const where: string[] = [];
              for (const g of aquiferGroups) {
                for (const wc of g.coords) {
                  if (watchIds.has(wc.well.well_name) || watchIds.has(wc.well.well_id)) {
                    where.push(`${wc.well.well_name}→${g.name} (elev=${wc.waterElev.toFixed(0)})`);
                  }
                }
              }
              return (
                <span className="ml-2 px-2 py-0.5 rounded border border-cyan-700/50 bg-cyan-900/20 text-cyan-300"
                  title={where.length ? `Watch wells:\n${where.join('\n')}` : 'Watch wells not found in any group'}>
                  {aquiferGroups.map(g => {
                    const elevs = g.coords.map(w => w.waterElev);
                    const min = Math.min(...elevs);
                    const max = Math.max(...elevs);
                    const mean = elevs.reduce((s, v) => s + v, 0) / elevs.length;
                    return `${g.name}:${g.coords.length} [${min.toFixed(0)}–${max.toFixed(0)}, μ=${mean.toFixed(0)}]`;
                  }).join(' · ')}
                  {where.length > 0 && ` · 🎯${where.length}`}
                </span>
              );
            })()}
          </div>
        </div>
      </div>
      <div style={{ height: '500px' }} onDoubleClick={() => setPropertyClipped(v => !v)}>
        <Canvas
          camera={{ position: [30, 20, 30], fov: 50, near: 0.1, far: 100000 }}
          gl={{ antialias: true, localClippingEnabled: true }}
        >
          <ambientLight intensity={0.5} />
          <directionalLight position={[markerScale * 50, markerScale * 80, markerScale * 50]} intensity={0.8} />
          <directionalLight position={[-markerScale * 50, markerScale * 60, -markerScale * 50]} intensity={0.3} />

          <AutoFitCamera
            wellCoords={wellCoords.map(w => ({ x: w.x, y: w.y, groundElev: w.groundElev, waterElev: w.waterElev }))}
            baseElev={baseElev}
            fitKey={`${aquiferFilter ?? 'all'}|${wellCoords.length}`}
          />
          <OrbitControls
            enableDamping
            dampingFactor={0.1}
            minDistance={1}
            maxDistance={Infinity}
          />

          {/* Property boundary (cyan rectangle from actual snip bounds) */}
          {(() => {
            const avgGroundElev = wellCoords.length > 0 ? wellCoords.reduce((s, w) => s + w.groundElev, 0) / wellCoords.length : 0;
            const boundaryElev = (avgGroundElev - baseElev) * SCALE - 0.5;

            // Check for multi-parcel snip
            const snipParcels = (propertyBounds as any)?.parcels;
            const boundsToRender = snipParcels && Array.isArray(snipParcels) && snipParcels.length > 0
              ? snipParcels.map((p: any) => p.bounds).filter(Boolean)
              : propertyBounds ? [{ north: propertyBounds.north, south: propertyBounds.south, east: propertyBounds.east, west: propertyBounds.west }]
              : [];

            // Fallback if no bounds at all
            if (boundsToRender.length === 0) {
              const sideFt = Math.sqrt(siteAcres * 43560);
              const cx = 0, cy = 0;
              return (
                <>
                  <PropertyBoundary centerX={cx} centerY={cy} sideX={sideFt * SCALE} sideY={sideFt * SCALE} elevation={boundaryElev} />
                  <PropertyZoom targetX={cx} targetY={cy} targetZ={boundaryElev} propSideX={sideFt * SCALE} propSideY={sideFt * SCALE} zoomed={propertyClipped} />
                </>
              );
            }

            // Render each parcel boundary
            const allBoundaries = boundsToRender.map((b: any, i: number) => {
              const minLatFt = (b.south - centerLat) * FT_PER_DEG_LAT;
              const maxLatFt = (b.north - centerLat) * FT_PER_DEG_LAT;
              const minLonFt = (b.west - centerLon) * FT_PER_DEG_LON_AT_32;
              const maxLonFt = (b.east - centerLon) * FT_PER_DEG_LON_AT_32;
              const cx = (minLonFt + maxLonFt) / 2 * SCALE;
              const cy = (minLatFt + maxLatFt) / 2 * SCALE;
              const sx = (maxLonFt - minLonFt) * SCALE;
              const sy = (maxLatFt - minLatFt) * SCALE;
              return <PropertyBoundary key={i} centerX={cx} centerY={cy} sideX={sx} sideY={sy} elevation={boundaryElev} />;
            });

            // Zoom to first parcel
            const first = boundsToRender[0];
            const fMinLat = (first.south - centerLat) * FT_PER_DEG_LAT;
            const fMaxLat = (first.north - centerLat) * FT_PER_DEG_LAT;
            const fMinLon = (first.west - centerLon) * FT_PER_DEG_LON_AT_32;
            const fMaxLon = (first.east - centerLon) * FT_PER_DEG_LON_AT_32;
            const fCx = (fMinLon + fMaxLon) / 2 * SCALE;
            const fCy = (fMinLat + fMaxLat) / 2 * SCALE;
            const fSx = (fMaxLon - fMinLon) * SCALE;
            const fSy = (fMaxLat - fMinLat) * SCALE;

            return (
              <>
                {allBoundaries}
                <PropertyZoom targetX={fCx} targetY={fCy} targetZ={boundaryElev} propSideX={fSx} propSideY={fSy} zoomed={propertyClipped} />
              </>
            );
          })()}

          {/* Ground surface (tan) — shared topography across all aquifers */}
          {showGround && (
            <GroundSurface
              wells={wellCoords.map(w => ({ x: w.x, y: w.y, groundElev: w.groundElev }))}
              baseElev={baseElev}
              gridSize={25}
            />
          )}

          {isCombined && aquiferGroups ? (
            /* Combined mode: each aquifer renders its own stacked surfaces.
               Water table = IDW from the group's wells. Aquifer base = parallel
               surface offset below water table by the group's median saturated
               thickness (avoids wild dips from per-well drilled depths). */
            aquiferGroups.map(group => {
              if (group.coords.length === 0) return null;

              const meanWater = group.coords.reduce((s, w) => s + w.waterElev, 0) / group.coords.length;
              const meanX = group.coords.reduce((s, w) => s + w.x, 0) / group.coords.length;
              const meanY = group.coords.reduce((s, w) => s + w.y, 0) / group.coords.length;
              const labelY = (meanWater - baseElev) * SCALE + 1.5 * markerScale;

              // Per-well bottom = water elev - group median thickness (parallel slab)
              const slabCoords = group.coords.map(w => ({
                x: w.x,
                y: w.y,
                waterElev: w.waterElev,
                bottomElev: w.waterElev - group.medianThickness,
              }));
              const baseCoords = slabCoords.map(w => ({ x: w.x, y: w.y, bottomElev: w.bottomElev }));

              const tooSparse = group.coords.length < 3;

              return (
                <group key={group.name}>
                  {showWater && !tooSparse && (
                    <SaturatedVolume
                      wells={slabCoords}
                      baseElev={baseElev}
                      gridSize={25}
                    />
                  )}
                  {showRedBed && !tooSparse && (
                    <AquiferBottomSurface
                      wells={baseCoords}
                      baseElev={baseElev}
                      gridSize={25}
                    />
                  )}
                  <Text
                    position={[meanX, labelY, meanY]}
                    fontSize={0.5 * markerScale}
                    color={tooSparse ? '#fbbf24' : '#67e8f9'}
                    anchorX="center"
                    anchorY="bottom"
                  >
                    {tooSparse
                      ? `${group.name} — ${group.coords.length} well${group.coords.length === 1 ? '' : 's'} (≥3 for surface)`
                      : `${group.name} (${group.coords.length})`}
                  </Text>
                </group>
              );
            })
          ) : (
            <>
              {/* Single-aquifer mode: one surface from filteredWells */}
              {showWater && (
                <SaturatedVolume
                  wells={wellCoords.map(w => ({ x: w.x, y: w.y, waterElev: w.waterElev, bottomElev: w.bottomElev }))}
                  baseElev={baseElev}
                  gridSize={25}
                />
              )}
              {showRedBed && (
                <AquiferBottomSurface
                  wells={wellCoords.map(w => ({ x: w.x, y: w.y, bottomElev: w.bottomElev }))}
                  baseElev={baseElev}
                  gridSize={25}
                />
              )}
              {showWater && (
                <WaterTableContours3D
                  wells={filteredWells}
                  centerLat={centerLat}
                  centerLon={centerLon}
                  baseElev={baseElev}
                />
              )}
            </>
          )}

          {/* Well columns */}
          {wellCoords.map(wc => (
            <WellColumn
              key={wc.well.well_id}
              well={wc.well}
              cx={wc.x}
              cy={wc.y}
              baseElev={baseElev}
              waterElev={wc.waterElev}
              cavitationRisk={wc.cavitationRisk}
              markerScale={markerScale}
            />
          ))}

          {/* Axis helpers */}
          <Text position={[0, -1, 0]} fontSize={0.5} color="#666" rotation={[-Math.PI / 2, 0, 0]}>
            N
          </Text>

        </Canvas>
      </div>
    </div>
  );
}
